import React, { createRef, useEffect, useRef } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline } from '@atlaskit/primitives/inline';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Stack } from '@atlaskit/primitives/stack';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss/xcss';

import { universalTypeFilterId } from '../../../../common/constants/filters';
import { ProductKeys } from '../../../../common/constants/products';
import { useSelectedProduct } from '../../../../controllers/store';
import { useFiltersToDisplay } from '../../../../controllers/store/search';
import { filter as assignee } from '../../../../controllers/store/search/filters/assignee';
import { filter as content } from '../../../../controllers/store/search/filters/content';
import { filter as contributor } from '../../../../controllers/store/search/filters/contributor';
import { filter as label } from '../../../../controllers/store/search/filters/label';
import { filter as lastUpdated } from '../../../../controllers/store/search/filters/last-updated';
import { filter as owner } from '../../../../controllers/store/search/filters/owner';
import { filter as project } from '../../../../controllers/store/search/filters/project';
import { filter as space } from '../../../../controllers/store/search/filters/space';
import { filter as titleMatchOnly } from '../../../../controllers/store/search/filters/title-match-only';
import { filter as verified } from '../../../../controllers/store/search/filters/verified';
import AssigneeFilter from './assignee';
import ContentFilter from './content';
import ContributorFilter from './contributor';
import DropdownFilter from './dropdown';
import LabelFilter from './label';
import LastUpdatedFilter from './last-updated';
import OwnerFilter from './owner';
import ProjectFilter from './project';
import { ResultsSort } from './results-sort';
import SpaceFilter from './space';
import TitleMatchOnlyFilter from './title-match-only';
import UniversalTypeFilter from './universal-type';
import VerifiedFilter from './verified';

const filters = {
	[lastUpdated.id]: LastUpdatedFilter,
	[universalTypeFilterId]: UniversalTypeFilter,
	[contributor.id]: ContributorFilter,
	[space.id]: SpaceFilter,
	[verified.id]: VerifiedFilter,
	[assignee.id]: AssigneeFilter,
	[project.id]: ProjectFilter,
	[content.id]: ContentFilter,
	[titleMatchOnly.id]: TitleMatchOnlyFilter,
	[owner.id]: OwnerFilter,
	[label.id]: LabelFilter,
};

const useOverflowFilterObserver = (
	containerRef: React.MutableRefObject<HTMLDivElement | undefined>,
	filterRefs: React.MutableRefObject<React.MutableRefObject<HTMLDivElement>[]>,
	filterIds: string[],
) => {
	const observerRef = useRef<IntersectionObserver | null>(null);

	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					const target = entry.target as HTMLElement;
					if (entry.isIntersecting) {
						target.style.visibility = 'visible';
					} else {
						target.style.visibility = 'hidden';
					}
				});
			},
			{
				root: containerRef.current,
				threshold: 0.85,
			},
		);
		observerRef.current = observer;
		return () => {
			observer.disconnect();
		};
	}, [containerRef]);

	useEffect(() => {
		const observer = observerRef.current;
		if (!observer) {
			return;
		}
		filterRefs.current.forEach((filterRef) => {
			observer.observe(filterRef?.current);
		});
	}, [filterRefs, filterIds]);
};

const flexStyles = xcss({
	overflow: 'hidden',
	flexWrap: 'wrap',
	padding: 'space.050',
});

const stackedStyles = xcss({
	maxWidth: '280px',
	padding: 'space.0',
});

const ProductFilters = (): React.JSX.Element => {
	const [{ filtersInLine }] = useFiltersToDisplay();
	const [productSelected] = useSelectedProduct();
	const filterContainerRef = useRef<HTMLDivElement>();
	const filterRefs = useRef<React.MutableRefObject<HTMLDivElement>[]>([]);
	const isConfluence = productSelected === ProductKeys.Confluence;

	filterRefs.current = filtersInLine
		.filter((id) => filters[id as keyof typeof filters])
		.map((_, i) => filterRefs.current[i] ?? createRef());

	useOverflowFilterObserver(filterContainerRef, filterRefs, filtersInLine);

	const renderInlineFilters = () => {
		return filtersInLine.map((id, index) => {
			const Filter = filters[id as keyof typeof filters];
			if (!Filter) {
				return null;
			}

			return (
				<Box key={id} ref={filterRefs.current[index]}>
					<Filter />
				</Box>
			);
		});
	};

	return (
		<Inline ref={filterContainerRef} space="space.100" xcss={flexStyles}>
			<DropdownFilter />
			{renderInlineFilters()}
			{isConfluence && <ResultsSort />}
		</Inline>
	);
};

export default ProductFilters;

export const StackedProductFilters = ({
	filtersToHide,
}: {
	filtersToHide: string[];
}): React.JSX.Element => {
	const [{ filtersInLine }] = useFiltersToDisplay();
	const renderInlineFilters = () => {
		return filtersInLine
			.filter((id) => !filtersToHide.includes(id))
			.map((id, index) => {
				const Filter = filters[id as keyof typeof filters];
				if (!Filter) {
					return null;
				}

				return (
					<Box key={id}>
						<Filter />
					</Box>
				);
			});
	};

	return (
		<Stack space="space.100" xcss={[flexStyles, stackedStyles]}>
			{renderInlineFilters()}
		</Stack>
	);
};
